import {
  Box,
  Button,
  Column,
  FormField,
  InputPasswordField,
  InputTextField,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useEffect, type KeyboardEvent } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { InvalidCredentialsError, useSignIn } from '~/domain/Auth';
import { removeWhitespace } from '~/utils/String';
import { AuthErrorBanner, useAuthFormSubmit, useTurnstileToken } from '../shared';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INVALID_CREDENTIALS_MESSAGE = 'Your email and password combination does not match.';

// DS Typography has no cursor prop — required for interactive non-button text
const LINK_STYLE = { cursor: 'pointer' } as const;

const activateOnSpaceOrEnter = (handler: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

export interface ILoginFormValues {
  email: string;
  password: string;
}

interface ILoginLocationState {
  prefillEmail?: string;
}

export const LoginForm = () => {
  const location = useLocation();
  const prefillEmail = (location.state as ILoginLocationState | null)?.prefillEmail ?? '';

  const methods = useForm<ILoginFormValues>({
    defaultValues: { email: prefillEmail, password: '' },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <LoginFormInner />
    </FormProvider>
  );
};

const LoginFormInner = () => {
  const navigate = useNavigate();
  const { mutateAsync: signIn, isPending, isError } = useSignIn();
  const {
    token: turnstileToken,
    widget: turnstileWidget,
    reset: resetTurnstile,
    enabled: turnstileEnabled,
  } = useTurnstileToken();

  const { submit, bannerVisible, bannerRef, serverMessage } = useAuthFormSubmit<ILoginFormValues>({
    onValid: async (values) => {
      try {
        await signIn({ ...values, turnstileToken });
      } catch (err) {
        // Surface wrong email/password inline (banner + red fields). Every
        // other error is already shown via the global pop-up, so swallow it
        // here to avoid a duplicate inline banner.
        if (err instanceof InvalidCredentialsError) throw err;
      }
    },
  });

  useEffect(() => {
    if (isError) resetTurnstile();
  }, [isError, resetTurnstile]);

  const {
    register,
    formState: { errors },
  } = useFormContext<ILoginFormValues>();

  const goToForgotPassword = () => navigate('/forgot-password');
  const goToSignup = () => navigate('/signup');

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: { value: EMAIL_REGEX, message: 'Enter a valid email' },
  });

  // A wrong email/password combo is a credentials error: the BE can't tell
  // which field is wrong, so both are highlighted while the banner is shown.
  const hasCredentialsError = Boolean(serverMessage);
  const emailHasError = Boolean(errors.email) || hasCredentialsError;
  const passwordHasError = Boolean(errors.password) || hasCredentialsError;

  return (
    <form onSubmit={submit} data-testid="login-form" noValidate>
      <Column pt={6} gap={8}>
        <Column gap={4}>
          <Typography bold size="heading_3" color={colors.primary[500]}>
            Welcome Back, Offices!
          </Typography>
          <Typography size="body_large_tight" color={colors.neutral[500]}>
            Log in to connect with trusted dental professionals and keep your schedule covered.
          </Typography>
        </Column>

        {/* Wrapper keeps a stable scrollIntoView target when the banner is hidden. */}
        <Box ref={bannerRef}>
          <AuthErrorBanner
            visible={bannerVisible}
            message={hasCredentialsError ? INVALID_CREDENTIALS_MESSAGE : undefined}
            prefix={hasCredentialsError ? '' : undefined}
          />
          <Column gap={8} pt={bannerVisible ? 3 : 0}>
            <FormField label="Email">
              <InputTextField
                data-cy="input-email"
                placeholder="Enter your email"
                isError={emailHasError}
                aria-invalid={emailHasError || undefined}
                {...emailRegister}
                onChange={(e) => {
                  const stripped = removeWhitespace(e.target.value);
                  if (stripped !== e.target.value) e.target.value = stripped;
                  emailRegister.onChange(e);
                }}
              />
            </FormField>
            <FormField label="Password">
              <InputPasswordField
                data-cy="input-password"
                placeholder="Enter your password"
                isError={passwordHasError}
                aria-invalid={passwordHasError || undefined}
                {...register('password', { required: 'Password is required' })}
              />
            </FormField>
          </Column>
        </Box>

        <Row justify="center">
          <Typography
            as="span"
            bold
            size="body_large"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="forgot-password-link"
            onClick={goToForgotPassword}
            onKeyDown={activateOnSpaceOrEnter(goToForgotPassword)}
          >
            Forgot Password?
          </Typography>
        </Row>

        {turnstileWidget}

        <Row justify="center">
          <Button
            data-cy="btn-log-in"
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isPending}
            disabled={turnstileEnabled && !turnstileToken}
          >
            Continue to GoTu
          </Button>
        </Row>

        <Row justify="center" align="center" wrap="nowrap" gap={1}>
          <Typography size="body_large" color={colors.neutral[500]}>
            Don&apos;t have an account?
          </Typography>
          <Typography
            as="span"
            bold
            size="body_large"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="link-sign-up"
            onClick={goToSignup}
            onKeyDown={activateOnSpaceOrEnter(goToSignup)}
          >
            Register Now
          </Typography>
        </Row>
      </Column>
    </form>
  );
};
